import React, { useEffect } from 'react';
import {
  Link,
  AddButton,
  AppContent,
  Currency,
  ErrorHandler,
} from '@common/components';
import { filter, noop } from 'lodash';

import { useQuery, useParams } from '@common/hooks';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { ManagerWorkerTimesheet } from '@timekeeping/graphql';
import {
  RenderFilteredTable,
  RenderTableColumn,
  RenderTableOnChange,
} from 'works/components/filtered-table/render-filtered-table';
import {
  ColumnTitleWithTooltip,
  WorkerTimesheetActionButtons,
  WorkerTimesheetPageTitle,
  WorkerTimesheetStats,
} from '@timekeeping/components';
import {
  EMPTY_PLACEHOLDER,
  formatEntryDates,
  formatLabelDate,
  formatSecondsToDuration,
  nonFalse,
} from '@timekeeping/services';
import {
  TableHourBlock,
  useHourBlocksForTable,
  HourBlockOrderFields,
  useSortedTableHourBlocks,
  useWorkerTimesheetStats,
  useHasHoliday,
} from '@timekeeping/hooks';
import { buildDialogUrl } from '@common/services';

export const WorkerTimesheetPage = () => {
  const { id = '' } = useParams<{ id: string }>();

  const { data, loading, error } = useQuery(ManagerWorkerTimesheet, {
    notifyOnNetworkStatusChange: true,
    variables: { id: id },
  });

  const timesheet = data?.timesheet;
  const worker = timesheet?.worksMembership;

  useEffect(() => {
    if (worker) {
      setBreadcrumbs([
        { name: 'Timecards', path: '/works/timecards' },
        { name: worker.name, path: `/works/timecard/${id}` },
      ]);
    }
  }, [id, worker]);

  const statsData = useWorkerTimesheetStats(timesheet);
  const rowBlocks = useHourBlocksForTable(timesheet?.hourBlocks);
  const hasHoliday = useHasHoliday(timesheet?.hourBlocks);
  const [effectiveHourBlocks, , setSort] = useSortedTableHourBlocks(rowBlocks);

  const columns = filter<
    RenderTableColumn<TableHourBlock> | false,
    RenderTableColumn<TableHourBlock>
  >(
    [
      {
        title: 'Shift Date',
        key: 'blockDate',
        className: 'block-date',
        dataIndex: 'date',
        width: '106px',
        sorter: true,
        render: (date, hourBlock) => (
          <Link
            className="text-heavy"
            to={buildDialogUrl('timekeeping-hour-block-review-drawer', {
              props: { hourBlockId: hourBlock.id },
            })}
          >
            {formatLabelDate(date)}
          </Link>
        ),
      },
      {
        title: 'Type',
        key: 'blockHourType',
        className: 'block-hour-type',
        dataIndex: 'hourType',
        width: '78px',
        sorter: true,
      },
      {
        title: 'Clock In / Out',
        key: 'blockClockInOut',
        className: 'block-in-out',
        dataIndex: 'inOut',
        width: '144px',
        sorter: true,
        render: (blockClockInOut, hourBlock) => (
          <div>
            <span className="color-midnight-lighter">
              <div>{formatEntryDates(hourBlock.inOutEntry)}</div>
            </span>
            <div>{blockClockInOut}</div>
          </div>
        ),
      },
      {
        title: 'Lunch / Break',
        key: 'blockLunchBreak',
        className: 'block-lunch-break',
        dataIndex: 'lunchBreak',
        width: '144px',
        sorter: true,
        render: (lunchBreak, hourBlock) => (
          <div>
            {hourBlock.lunchBreakEntry && (
              <span className="color-midnight-lighter">
                <div>{formatEntryDates(hourBlock.lunchBreakEntry)}</div>
              </span>
            )}
            <div>{lunchBreak || EMPTY_PLACEHOLDER}</div>
          </div>
        ),
      },
      {
        title: 'Total Time',
        key: 'blockTotalTime',
        className: 'block-total-time',
        dataIndex: 'totalTime',
        width: '86px',
        sorter: true,
      },
      {
        title: () => {
          const seconds = timesheet?.overtimeThresholdSeconds;
          const duration = formatSecondsToDuration(seconds || 0);

          return (
            <ColumnTitleWithTooltip
              hasTooltip={!!seconds}
              tooltip={`Overtime threshold is ${duration}`}
            >
              Overtime
            </ColumnTitleWithTooltip>
          );
        },
        key: 'blockTotalOvertime',
        className: 'block-total-overtime',
        dataIndex: 'totalOvertime',
        width: '96px',
      },
      hasHoliday && {
        title: 'Holiday',
        key: 'blockTotalHoliday',
        className: 'block-total-holiday',
        dataIndex: 'totalHoliday',
        width: '96px',
      },
      {
        title: 'Rate',
        key: 'billingRate',
        dataIndex: 'billingRate',
        sorter: true,
        width: '106px',
        render: (billingRate) =>
          billingRate ? <Currency value={billingRate} /> : EMPTY_PLACEHOLDER,
      },
      {
        title: 'Unit / Location',
        key: 'blockUnitLocation',
        className: 'block-unit-location',
        dataIndex: 'unitAndLocation',
        sorter: true,
        render: ([unit, location]) => (
          <div>
            <div>{unit}</div>
            <span className="color-midnight-lighter">
              <small>{location}</small>
            </span>
          </div>
        ),
      },
      {
        title: 'CC',
        key: 'blockCostCenter',
        className: 'block-cost-center',
        dataIndex: 'costCenter',
        width: '78px',
        sorter: true,
      },
      {
        title: 'Notes',
        key: 'blockNotes',
        className: 'block-notes',
        dataIndex: 'notes',
        sorter: true,
        ellipsis: true,
      },
      {
        className: 'hour-block-action',
        width: '78px',
        render: (_: unknown, hourBlock: TableHourBlock) =>
          hourBlock.workerTimesheet.canManagerUpsertHours.value &&
          hourBlock.state !== 'deleted' && (
            <Link
              className="hour-block-edit-button"
              to={buildDialogUrl('timekeeping-hour-block-edit-drawer', {
                props: { hourBlockId: hourBlock.id },
              })}
            >
              <strong>Edit</strong>
            </Link>
          ),
      },
    ],
    nonFalse
  );

  const handleTableChange: RenderTableOnChange<TableHourBlock> = (
    _,
    __,
    sorter
  ) => {
    if (sorter instanceof Array) {
      return;
    }

    setSort({
      direction: sorter.order,
      field: sorter.field as HourBlockOrderFields,
    });
  };

  if (!loading && (error || !timesheet)) {
    return <ErrorHandler error={error} log="OrganizationTimesheetPage" />;
  }

  // FIXME: assignmentID needs to be a field in the worker timesheet/timecard
  //  to communicate intent. (leadingAssignment)
  return (
    <AppContent>
      <div className="header-container">
        <span className="title-container">
          <WorkerTimesheetPageTitle
            workerName={worker?.name || ''}
            status={timesheet?.status}
            assignmentId={timesheet?.staffingRequestAssignments[0]?.id}
          />
        </span>
        {timesheet && (
          <WorkerTimesheetActionButtons mode="loose" timesheet={timesheet} />
        )}
      </div>
      <div className="summary-container">
        <WorkerTimesheetStats data={statsData} />
      </div>
      <div className="button-container">
        {timesheet?.canManagerUpsertHours.value && (
          <Link
            to={buildDialogUrl('timekeeping-add-hour-block-drawer', {
              props: { timecardId: id },
            })}
          >
            <AddButton>Add Hours</AddButton>
          </Link>
        )}
      </div>
      <div className="table-wrapper">
        <RenderFilteredTable
          rowKey="id"
          className="hour-blocks-table"
          dataSource={effectiveHourBlocks}
          columns={columns}
          loading={loading}
          onChange={handleTableChange}
          pagination={false}
          filters={{}}
          onClearFilter={noop}
          onClearAllFilters={noop}
          rowClassName={(record) =>
            record.state === 'deleted' ? 'disabled-row deleted' : undefined
          }
        />
      </div>
      <style jsx>{`
        .header-container {
          display: flex;
          flex-direction: row;
          align-items: center;
        }
        .title-container {
          flex: 1;
        }
        :global(.hour-blocks-table .ant-table-row) {
          height: 49px;
        }
        .summary-container {
          padding-top: 17px;
          padding-bottom: 32px;
        }
        .button-container {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 24px;
          margin-top: -44px;
        }
        .table-wrapper :global(.deleted .ant-table-cell:not(.block-date)) {
          text-decoration: line-through;
        }
      `}</style>
    </AppContent>
  );
};
